/**
 * CharacterMiniStrip — Compact character status bar for the grid map game view.
 * Shows portrait, name, vitals bar, and resolve bar in a single horizontal strip.
 * Currently uses mock data; structured for easy Zustand integration later.
 */

import { Heart, Zap, User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CharacterData {
  name: string;
  imageUrl: string | null;
  vitals: { current: number; max: number };
  resolve: { current: number; max: number };
}

const MOCK_CHARACTER: CharacterData = {
  name: 'Kael Stormborn',
  imageUrl: null,
  vitals: { current: 7, max: 10 },
  resolve: { current: 4, max: 6 },
};

function getVitalsColor(pct: number): string {
  if (pct > 0.6) return 'bg-emerald-500';
  if (pct > 0.3) return 'bg-amber-500';
  return 'bg-red-500';
}

export function CharacterMiniStrip({ character = MOCK_CHARACTER }: { character?: CharacterData }) {
  const vitalsPct = character.vitals.current / character.vitals.max;
  const resolvePct = character.resolve.current / character.resolve.max;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-neutral-800 bg-neutral-950">
      {/* Portrait */}
      {character.imageUrl ? (
        <img
          src={character.imageUrl}
          alt={character.name}
          className="w-10 h-10 rounded-lg object-cover border border-white/10"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-neutral-800 border border-white/10 flex items-center justify-center">
          <User className="w-5 h-5 text-neutral-500" />
        </div>
      )}

      {/* Name + bars */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-200 truncate">{character.name}</p>
        <div className="mt-1 space-y-1">
          {/* Vitals bar */}
          <div className="flex items-center gap-1.5">
            <Heart className="w-3 h-3 text-red-500 shrink-0" />
            <div className="flex-1 h-1.5 bg-neutral-900 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', getVitalsColor(vitalsPct))}
                style={{ width: `${vitalsPct * 100}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-neutral-500 w-8 text-right">
              {character.vitals.current}/{character.vitals.max}
            </span>
          </div>
          {/* Resolve bar */}
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-blue-500 shrink-0" />
            <div className="flex-1 h-1.5 bg-neutral-900 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${resolvePct * 100}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-neutral-500 w-8 text-right">
              {character.resolve.current}/{character.resolve.max}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
